import styles from "./styles/edit-custom-modal.module.scss";
import Image from "next/image";
import uploadIcon from "@/assets/convert/upload.svg";
import React, { ChangeEvent, useState } from "react";
import CheckBox from "@/components/common/checkbox";
import FilledButton from "@/components/common/filled-button";
import UnfilledButton from "@/components/common/unfilled-button";
import useEditCustom from "@/components/pages/custom/hooks/use-edit-custom";
import validateField from "@/components/pages/custom/utils/field-validator";
import style from "@/components/pages/custom/custom-page.module.scss";
import Loader from "@/components/common/loader";
import StyledInput from "@/components/common/input/styled-input";
import { Tooltip } from "react-tooltip";
import CoverTooltip from "@/components/pages/convert/components/cover-tooltip";

type Props = {
  voiceId: string;
  voiceName: string;
};

const EditCustomModal = ({ voiceId, voiceName }: Props) => {
  const {
    name,
    setName,
    newCover,
    setNewCover,
    makePublic,
    isPrivacyEditingAllowed,
    setMakePublic,
    saveChanges,
    saveLoading,
  } = useEditCustom(voiceName);
  const handleMakePublic = () => {
    setMakePublic(!makePublic);
  };

  const [image, setImage] = useState<string | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleVoiceName = (value: string) => {
    setName(value);
    setErrors((prevErrors) => ({ ...prevErrors, voiceName: "" }));
  };

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (!(e.target.files && e.target.files[0])) {
      return;
    }

    const file = e.target.files[0];
    setNewCover(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    setErrors((prevErrors) => ({ ...prevErrors, image: "" }));
  };

  const handleSaveChanges = async () => {
    const voiceNameValidation = await validateField("voiceNameEdit", name);
    const imageValidation = await validateField("image", newCover);

    if (!imageValidation.isValid || !voiceNameValidation.isValid) {
      setErrors({
        voiceName: voiceNameValidation.message,
        image: imageValidation.message,
      });
      return;
    }

    await saveChanges(voiceId);
    window.location.reload();
  };

  const input = (
    <input
      id="fileInput"
      type="file"
      accept=".png, .jpg, .jpeg, .webp"
      onChange={handleImageUpload}
      style={{ display: "none" }}
    />
  );

  const handleInputClick = () => {
    document.getElementById("fileInput")?.click();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>{voiceName}</div>
      <div className={styles.nameInput}>
        <StyledInput
          title="Name"
          value={name}
          onChange={(element) => handleVoiceName(element.target.value)}
        />
      </div>
      {errors.voiceName && (
        <div className={style.error}>{errors.voiceName}</div>
      )}
      {input}
      {image ? (
        <div className={styles.new_image_wrapper}>
          <Image src={image} alt={"new_thumbnail"} width={170} height={160} />
          <UnfilledButton
            borderColor={"#F9A825"}
            text={"Upload another cover"}
            onClick={handleInputClick}
          />
        </div>
      ) : (
        <div className={styles.imageContainer} onClick={handleInputClick}>
          <Image
            className={styles.uploadLogo}
            width={60}
            src={uploadIcon}
            alt="Upload"
          />
          <div>Upload Cover</div>
        </div>
      )}

      {errors.image && <div className={style.error}>{errors.image}</div>}
      <>
        <CoverTooltip id={"privacyCheckBox"} />
        <div
          className={styles.publicCheckbox}
          data-tooltip-id="privacyCheckBox"
          data-tooltip-content={
            !isPrivacyEditingAllowed
              ? "Public voices are only available to paid users"
              : undefined
          }
        >
          <CheckBox
            isChecked={makePublic ?? false}
            onChange={handleMakePublic}
            isEnabled={isPrivacyEditingAllowed}
          />
          <div className={styles.checkboxTitle}>Make Public</div>
        </div>
      </>
      <div className={styles.saveButton}>
        {saveLoading ? (
          <Loader />
        ) : (
          <FilledButton text="Save Changes" onClick={handleSaveChanges} />
        )}
      </div>
    </div>
  );
};

export default EditCustomModal;
